<template>
    <div id="mermaid" class="mermaid">{{ parseCode }}</div>
</template>

<script>
import mermaid from "mermaid"

export default {
    name: "ProcessVisualizer",
    props: {
        type: {
            type: String,
            default: "graph TD"
        },
        nodes: {
            type: Array,
            required: true
        },
        styles: {
            type: Array,
            default() {
                return []
            }
        },
        config: {
            type: Object,
            default() {
                return {}
            }
        },
        defaultConfig: {
            type: Object,
            default() {
                return {
                    theme: 'neutral',
                    fontFamily: 'Mono',
                    fontSize: "12",
                    nodeSpacing: "5",
                    rankSpacing: "5",
                    startOnLoad: false,
                    securityLevel: "loose"
                }
            }
        },
        stopOnError: {
            type: Boolean,
            default: false
        }
    },
    data: function () {
        return {
            edges: [
                { type: "rectangle", open: "[", close: "]" },
                { type: "default", open: "(", close: ")" },
                { type: "circle", open: "((", close: "))" },
                { type: "asymetric", open: ">", close: "]" },
                { type: "rhombus", open: "{", close: "}" }
            ]
        }
    },
    watch: {
        nodes: {
            immediate: true,
            handler: function () {
                this.$nextTick(() => {
                    let element = document.querySelector('.running');
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
        }
    },
    mounted() {
        this.init()
        this.loadNodes()
    },
    computed: {
        nodeObject() {
            const { nodes } = this
            if (Array.isArray(nodes) && nodes.length > 0) {
                const arrayToObject = (arr, keyField) =>
                    Object.assign({}, ...arr.map(item => ({ [item[keyField]]: item })))
                return arrayToObject(nodes, "id")
            } else {
                return {}
            }
        },
        customStyle() {
            const { nodes, styles } = this
            const nodeStyles = nodes
                .filter(node => node.style)
                .map(node => `style ${node.id} ${node.style}`)
            return nodeStyles.concat(styles)
        },
        parseCode() {
            const { nodes } = this
            if (Array.isArray(nodes) && nodes.length > 0) {
                const parseCode = this.type + "\n"
                const groupNodes = this.getGroupNodes(nodes)
                const code = parseCode + groupNodes + this.customStyle.join(" \n")
                this.load(code)
                return code
            } else {
                return ""
            }
        }
    },
    methods: {
        getGroupNodes(nodes) {
            const innerMap = new Map()
            nodes.forEach(element => {
                const group = element.group || ""
                const data = innerMap.get(group) || { nids: new Set(), narr: [] }
                data.nids.add(element.id)
                data.narr.push(element)
                innerMap.set(group, data)
            })
            return [...innerMap.entries()]
                .map(item => {
                    const [groupName, entry] = item
                    const { nids, narr } = entry
                    if (groupName !== "") {
                        const innerNodes = []
                        const outNodes = []
                        narr.forEach(node => {
                            if (node.next) {
                                innerNodes.push({
                                    id: node.id,
                                    text: node.text,
                                    style: node.style
                                })
                                node.next.forEach(id => {
                                    if (nids.has(id)) {
                                        innerNodes.push({
                                            id: node.id,
                                            text: node.text,
                                            link: node.link,
                                            next: [id]
                                        })
                                    } else {
                                        outNodes.push({
                                            id: node.id,
                                            text: node.text,
                                            link: node.link,
                                            next: [id]
                                        })
                                    }
                                })
                            } else {
                                innerNodes.push(node)
                            }
                        })
                        const innerNodesStr = this.buildNodesStr(innerNodes)
                        const outNodeStr = this.buildNodesStr(outNodes)
                        return `subgraph ${groupName} \n ${innerNodesStr} end \n ${outNodeStr}`
                    } else {
                        const nodesStr = this.buildNodesStr(narr)
                        return nodesStr
                    }
                })
                .join(" \n")
        },
        buildNodesStr(nodes) {
            return (
                nodes
                    .map(item => {
                        if (item.next && item.next.length > 0) {
                            return item.next
                                .map((n, index) => {
                                    const next = this.nodeObject[n] || this.nodeObject[n.id]
                                    if (next != null && typeof next != "undefined") {
                                        return `${this.buildNode(item)}${this.buildLink(
                                            item,
                                            index
                                        )}${this.buildNode(next)}`
                                    } else {
                                        //TODO error
                                        return `${this.buildNode(item)}`
                                    }
                                })
                                .join("\n")
                        } else {
                            return `${this.buildNode(item)}`
                        }
                    })
                    .join("\n") +
                "\n" +
                nodes
                    .filter(item => item.editable)
                    .map(item => {
                        return `click ${item.id} mermaidClick`
                    })
                    .join("\n") +
                "\n" +
                nodes
                    .filter(item => item.class)
                    .map(item => {
                        return item.class
                            .split(",")
                            .map(it => {
                                return `class ${item.id} ${it}`
                            })
                            .join("\n")
                    })
                    .join("\n") +
                "\n"
            )
        },
        buildNode(item) {
            let edge = !item.edgeType
                ? this.edges.find(e => {
                    return e.type === "default"
                })
                : this.edges.find(e => {
                    return e.type === item.edgeType
                })
            let nodeText = item.text
                .split("<br/>")
                .map(textPart => {
                    let maxLength = 25;
                    if (textPart.length > maxLength) {
                        return textPart.substring(0, maxLength - 4) + "..."
                    } else {
                        let currentLength = textPart.length;
                        let missingCharCount = maxLength - currentLength;
                        let suffix = "_".repeat(missingCharCount)
                        return textPart + "<span>" + suffix + "</span>"
                    }
                })
                .join("<br/>")
            return `${item.id}${edge.open}${nodeText}${edge.close}`
        },
        buildLink(item, index) {
            const link = "-->"
            if (item.link) {
                if (Array.isArray(item.link)) {
                    if (item.link.length > index) return item.link[index]
                    else return item.link[item.link.length - 1]
                } else {
                    return item.link
                }
            }
            return link
        },
        loadNodes() {
            this.load(this.parseCode)
        },
        init() {
            const _t = this
            window.mermaidClick = function (id) {
                _t.edit(id)
            }
            mermaid.initialize(Object.assign(this.defaultConfig, this.config))
        },
        load(code) {
            if (code) {
                var container = document.getElementById("mermaid")
                if (container) {
                    container.removeAttribute("data-processed")
                    container.replaceChild(
                        document.createTextNode(code),
                        container.firstChild
                    )
                    try {
                        mermaid.init(code, container)
                    } catch (error) {
                        if (this.stopOnError) {
                            throw error
                        }
                    }
                }
            }
        },
        edit(id) {
            this.$emit("nodeClick", id)
        }
    }
}
</script>

<style>
.mermaid g.node {
    filter: drop-shadow(0px 3px 1px rgba(0, 0, 0, 0.2)) drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.14)) drop-shadow(0px 1px 5px rgba(0, 0, 0, 0.12));
}

.mermaid g.node rect {
    stroke: none !important;
    fill: white !important;
    stroke-linecap: round;
}

.mermaid g.node>g.label {
    text-align: left !important;
    /*font-family: 'Mono', monospace !important;*/
    /*font-size: 12px;*/
    /*line-height: 14px;*/
}

.mermaid g.node foreignObject>div {
    /*margin-top: 6px;*/
}

.mermaid g.node.multiline foreignObject>div {
    /*margin-top: 12px;*/
}

.mermaid g.node.idle rect,
.mermaid g.node.substep.idle rect {
    fill: #ffffff !important;
}

.mermaid g.node.running rect {
    fill: #e7a34d !important;
}

.mermaid g.node.substep.running rect {
    fill: #47a6d1 !important;
}

.mermaid g.node.completed rect,
.mermaid g.node.substep.completed rect {
    fill: #42b983 !important;
}

.mermaid g.node.disabled rect,
.mermaid g.node.substep.disabled rect {
    fill: #dddddd !important;
}

.mermaid g.node.completed>g.label,
.mermaid g.node.running>g.label,
.mermaid g.node.substep.running>g.label {
    color: #fff !important;
}

.mermaid g.node>g.label span {
    color: transparent !important;
}

.mermaid svg {
    -webkit-filter: drop-shadow(0px 3px 1px rgba(0, 0, 0, 0.2)) drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.14)) drop-shadow(0px 1px 5px rgba(0, 0, 0, 0.12));
    filter: drop-shadow(0px 3px 1px rgba(0, 0, 0, 0.2)) drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.14)) drop-shadow(0px 1px 5px rgba(0, 0, 0, 0.12));

}
</style>
